/**
 * Persistence.
 *
 * IndexedDB, not localStorage: the game snapshot is structured, it is written
 * on every move, and localStorage is synchronous and unreachable from a service
 * worker. Settings stay in localStorage (see settings.ts) because they are two
 * dozen bytes read once at boot.
 *
 * Every call here is best-effort. A player in a private window with storage
 * blocked should still get a working game, just not a resumable one.
 */

import { del, get, set } from "idb-keyval";
import type { GameSnapshot } from "./game";
import type { Tier } from "../core/techniques/types";

const CURRENT_GAME = "sudoku.game.v1";
const STATS = "sudoku.stats.v1";

export async function saveGame(snapshot: GameSnapshot): Promise<void> {
  try {
    await set(CURRENT_GAME, snapshot);
  } catch {
    /* storage unavailable */
  }
}

export async function loadGame(): Promise<GameSnapshot | null> {
  try {
    return (await get<GameSnapshot>(CURRENT_GAME)) ?? null;
  } catch {
    return null;
  }
}

export async function clearGame(): Promise<void> {
  try {
    await del(CURRENT_GAME);
  } catch {
    /* storage unavailable */
  }
}

export interface TierStats {
  played: number;
  solved: number;
  bestMs: number | null;
  /** Every solve time, so the median stays honest as more games land. */
  timesMs: number[];
}

export type Stats = Partial<Record<Tier | "unrated", TierStats>>;

function emptyTier(): TierStats {
  return { played: 0, solved: 0, bestMs: null, timesMs: [] };
}

export async function loadStats(): Promise<Stats> {
  try {
    return (await get<Stats>(STATS)) ?? {};
  } catch {
    return {};
  }
}

export async function recordStarted(tier: Tier | "unrated"): Promise<void> {
  const stats = await loadStats();
  const entry = stats[tier] ?? emptyTier();
  entry.played++;
  stats[tier] = entry;
  try {
    await set(STATS, stats);
  } catch {
    /* storage unavailable */
  }
}

export async function recordSolved(tier: Tier | "unrated", ms: number): Promise<void> {
  const stats = await loadStats();
  const entry = stats[tier] ?? emptyTier();
  entry.solved++;
  entry.timesMs.push(ms);
  entry.bestMs = entry.bestMs === null ? ms : Math.min(entry.bestMs, ms);
  stats[tier] = entry;
  try {
    await set(STATS, stats);
  } catch {
    /* storage unavailable */
  }
}

/** Median beats mean here: one interrupted game should not skew the number. */
export function medianMs(times: readonly number[]): number | null {
  if (times.length === 0) return null;
  const sorted = [...times].sort((a, b) => a - b);
  const middle = sorted.length >> 1;
  return sorted.length % 2 === 1
    ? sorted[middle]!
    : Math.round((sorted[middle - 1]! + sorted[middle]!) / 2);
}
